/**
 * Agreements API Client
 *
 * API functions for employee agreements management (cross-evaluation view).
 */

import { apiRequest } from './api';
import type { ScoreAgreement, AgreementUpdate, AgreementUpdateInput } from './evaluationsApi';

// Types
export interface EmployeeAgreement {
  id: number;
  evaluationId: number;
  evaluationPeriod: {
    start: string;
    end: string;
  } | null;
  type: string;
  typeName: string;
  typeIcon: string;
  typeColor: string;
  beschrijving: string;
  deadline: string | null;
  isCompleted: boolean;
  isEmployeeActionHolder?: boolean;
  createdAt: string;
}

export interface EmployeeAgreementsResponse {
  data: EmployeeAgreement[];
  byEvaluation: Record<number, EmployeeAgreement[]>;
}

// API functions
export const agreementsApi = {
  /**
   * Get all agreements for an employee across all evaluations
   */
  async getEmployeeAgreements(employeeId: number): Promise<EmployeeAgreementsResponse> {
    return apiRequest<EmployeeAgreementsResponse>(`/agreements/${employeeId}`);
  },

  /**
   * Get action holder agreements for an employee
   * Returns open agreements where employee is responsible for follow-up
   */
  async getActionHolderAgreements(employeeId: number): Promise<{ data: ScoreAgreement[] }> {
    return apiRequest(`/agreements/action-holder/${employeeId}`);
  },

  /**
   * Get team agreements for a manager
   * Returns all open agreements for the manager's team members
   */
  async getTeamAgreements(): Promise<{ data: ScoreAgreement[] }> {
    return apiRequest('/agreements/team');
  },

  /**
   * Get a single agreement with all updates
   */
  async getAgreementWithUpdates(agreementId: number): Promise<{ data: ScoreAgreement }> {
    return apiRequest(`/agreements/score/${agreementId}`);
  },

  /**
   * Add a progress update to an agreement
   */
  async addUpdate(agreementId: number, input: AgreementUpdateInput): Promise<{ data: AgreementUpdate }> {
    return apiRequest(`/agreements/score/${agreementId}/updates`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  /**
   * Get all open agreements across the organization (HR only)
   * Returns all open agreements for HR dashboard
   */
  async getAllAgreements(): Promise<{ data: ScoreAgreement[] }> {
    return apiRequest('/agreements/all');
  },

  /**
   * Download attachment from an agreement update
   * Returns the attachment data for download
   */
  async getUpdateAttachment(updateId: number): Promise<{ data: { name: string; type: string; data: string } }> {
    return apiRequest(`/agreements/updates/${updateId}/attachment`);
  },

  /**
   * Delete (archive) a score agreement
   * Only beoordelaar, hr, and admin can delete (NOT medewerker)
   */
  async deleteAgreement(agreementId: number): Promise<{ success: boolean; message: string }> {
    return apiRequest(`/agreements/score/${agreementId}`, {
      method: 'DELETE',
    });
  },

  /**
   * Get all archived (deleted) agreements (admin only)
   */
  async getArchivedAgreements(): Promise<{ data: ScoreAgreement[] }> {
    return apiRequest('/agreements/archived');
  },

  /**
   * Update an agreement's description (only creator can edit when in concept)
   */
  async updateAgreement(agreementId: number, beschrijving: string): Promise<{ data: ScoreAgreement }> {
    return apiRequest(`/agreements/score/${agreementId}`, {
      method: 'PATCH',
      body: JSON.stringify({ beschrijving }),
    });
  },

  /**
   * Update an agreement update's content (only author can edit)
   */
  async updateAgreementUpdate(updateId: number, content: string): Promise<{ data: AgreementUpdate }> {
    return apiRequest(`/agreements/updates/${updateId}`, {
      method: 'PATCH',
      body: JSON.stringify({ content }),
    });
  },
};
